import React, { useEffect, useRef, useState } from "react";
import { Form, Input } from "semantic-ui-react";
import { Box, Button, CircularProgress, Typography, Alert } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import customHttp from "../../services/customHTTP";
import { useAuth } from "../../context/authContext";
import "../Login/Login.css";
import { ASSETS_BASE_URL } from "../../config";

const VerifyEmail = () => {
	const [error, setError] = useState(""); // General error message
	const [loading, setLoading] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [resendLoading, setResendLoading] = useState(false);
	const [navLoading, setNavLoading] = useState(false);
	const { login, isAuthenticated, user, updateUserStatus } = useAuth(); // Access the login function from AuthContext
	const [otp, setOtp] = useState("");
	const navigate = useNavigate();
	const location = useLocation();
	const [flowToken, setFlowToken] = useState(location.state?.flowToken || sessionStorage.getItem("registration_flow_token") || null);

	const AuthPageStyles = {
		backgroundImage: `url('${ASSETS_BASE_URL}/home_bg2.jpg')`,
		backgroundSize: "cover",
		backgroundPosition: "center",
	};

	const combinedErrorMessage = [error].filter(Boolean).join(" | ");

	const hasSent = useRef(false);

	useEffect(() => {
		if (!flowToken && !isAuthenticated) {
			setError("Verification session missing. Please start the registration process again.");
		}
	}, [flowToken, isAuthenticated]);

	useEffect(() => {
		if (flowToken) {
			sessionStorage.setItem("registration_flow_token", flowToken);
			return;
		}

		if (isAuthenticated && user && !hasSent.current) {
			const alreadySent = localStorage.getItem("otp_sent");

			if (!alreadySent) {
				customHttp
					.post("/auth/send-otp", {
						user_id: user.user_id,
						force_resend: true,
					})
					.then((response) => {
						if (!response?.status) {
							throw new Error(response?.message || "Failed to send OTP");
						}
						if (response?.data?.flow_token) {
							setFlowToken(response.data.flow_token);
							sessionStorage.setItem("registration_flow_token", response.data.flow_token);
						}
						hasSent.current = true;
						localStorage.setItem("otp_sent", "true");
					})
					.catch((error) => {
						const msg =
							error?.response?.data?.message ||
							error?.response?.data?.error_message ||
							error?.message ||
							"Failed to resend OTP.";
						setError(msg);
					});
			}
		}
	}, [flowToken, isAuthenticated, user]);

	const handleVerifyEmail = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		try {
			if (!flowToken && !user?.user_id) {
				throw new Error("Verification session missing. Please register again.");
			}

			const response = await customHttp.post("/auth/verify-email", {
				otp,
				...(flowToken ? { flow_token: flowToken } : { user_id: user.user_id }),
			});
			if (response.status === 200) {
				setSuccessMessage("Email verified successfully");
				setLoading(false);
				const isRegistrationFlow = !!flowToken;
				if (isRegistrationFlow) {
					setNavLoading(true);
					sessionStorage.removeItem("registration_flow_token");
					localStorage.removeItem("otp_sent");
					setTimeout(() => {
						navigate("/login");
						setNavLoading(false);
					}, 1200);
				} else {
					updateUserStatus({ email_verified: true, email_verified_date: new Date(), first_login: false });
					setNavLoading(true);
					setTimeout(() => {
						navigate("/");
						localStorage.removeItem("otp_sent");
						setNavLoading(false);
					}, 1000);
				}
			}
		} catch (error) {
			const msg =
				error?.response?.data?.message ||
				error?.response?.data?.error_message ||
				error?.message ||
				"Verification failed.";
			setError(msg);
			setLoading(false);
		}
	};

	const handleResendOtp = async () => {
		setError("");
		setResendLoading(true);
		try {
			if (!flowToken && !user?.user_id) {
				throw new Error("Verification session missing. Please register again.");
			}

			const response = await customHttp.post("/auth/send-otp", {
				...(flowToken ? { flow_token: flowToken } : { user_id: user.user_id }),
				force_resend: true,
			});
			if (response.status === 200) {
				if (response.data?.flow_token) {
					setFlowToken(response.data.flow_token);
					sessionStorage.setItem("registration_flow_token", response.data.flow_token);
				}
				setSuccessMessage("OTP sent to your email address");
				setResendLoading(false);
			}
		} catch (error) {
			const msg =
				error?.response?.data?.message ||
				error?.response?.data?.error_message ||
				error?.message ||
				"Failed to resend OTP.";
			setError(msg);
			setResendLoading(false);
		}
		setTimeout(() => {
			setSuccessMessage("");
		}, 3000);
	};

	return (
		<Box className="login-container" sx={AuthPageStyles}>
			<Box className="login-form-container">
				<img src="/logo.png" alt="Logo" className="login-logo" />
				<div className="form-container">
					<PageTitle title="Verify Email" />

					{combinedErrorMessage && (
						<Alert severity="error" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{combinedErrorMessage}
						</Alert>
					)}
					{successMessage && successMessage !== "" && (
						<Alert severity="success" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{successMessage}
						</Alert>
					)}
					<Form className="login-form" onSubmit={handleVerifyEmail} error={!!combinedErrorMessage}>
						<Form.Field error={!!error}>
							<Input
								fluid
								placeholder="Enter OTP"
								type="tel"
								className="login-input"
								value={otp}
								onChange={(e) => setOtp(e.target.value)}
							/>
						</Form.Field>
						<Box mt={2} mb={2} textAlign="center">
							<Typography variant="body1" textAlign="center" color="text.secondary" fontSize={12}>
								{flowToken
									? "Enter the OTP sent to your email address to verify your registration. If you haven't received an OTP,"
									: "Enter the OTP sent to your email address to verify your account. If you haven't received an OTP,"}
								<Link
									onClick={handleResendOtp}
									className="login-link"
									style={{ fontSize: "12px", marginLeft: "5px" }}>
									Resend OTP
								</Link>
							</Typography>
						</Box>
						<div className="login-button">
							<Button
								variant="contained"
								color="primary"
								className="button-primary"
								type="submit"
								sx={{ minWidth: "120px" }}
								disabled={loading}>
								{loading ? "Verifying..." : "Verify"}
							</Button>
						</div>
						{resendLoading && (
							<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
								<CircularProgress size={24} sx={{ color: "green" }} />
								<Typography variant="body2" sx={{ marginLeft: 2, color: "green" }}>
									Resending OTP...
								</Typography>
							</Box>
						)}
						{navLoading && (
							<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
								<CircularProgress size={24} sx={{ color: "green" }} />
								<Typography variant="body2" sx={{ marginLeft: 2, color: "green" }}>
									{flowToken ? "Redirecting to login page..." : "Navigating to home page..."}
								</Typography>
							</Box>
						)}
					</Form>
				</div>
			</Box>
		</Box>
	);
};

export default VerifyEmail;
